"use client";

import { forwardRef } from "react";
import { cn } from "~/lib/utils";
import { ArrowLeftIcon, ArrowRightIcon } from "~/components/icons";

export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];

  pages.push(1);

  if (currentPage > 3) {
    pages.push("...");
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (currentPage < totalPages - 2) {
    pages.push("...");
  }

  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  ({ className, currentPage, totalPages, onPageChange, disabled = false, ...props }, ref) => {
    const pages = generatePageNumbers(currentPage, totalPages);
    const canGoPrevious = currentPage > 1 && !disabled;
    const canGoNext = currentPage < totalPages && !disabled;

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-center gap-2 px-0 py-2",
          className
        )}
        {...props}
      >
        {/* Desktop Layout */}
        <div className="hidden items-center justify-center gap-2 md:flex">
          {/* Previous Button */}
          <button
            type="button"
            onClick={() => canGoPrevious && onPageChange(currentPage - 1)}
            disabled={!canGoPrevious}
            className={cn(
              "flex min-h-[40px] items-center justify-center gap-1.5 px-3.5 py-2",
              "text-sm font-semibold leading-5 text-[rgba(0,0,0,0.7)]",
              "transition-colors hover:bg-[#fafafa]",
              "disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            )}
          >
            <ArrowLeftIcon className="h-5 w-5 text-[#60646c]" />
            <span>Previous</span>
          </button>

          {/* Page Numbers Group */}
          <div className="flex overflow-clip rounded-md border border-[rgba(0,0,45,0.09)]">
            {pages.map((page, index) => {
              if (page === "...") {
                return (
                  <div
                    key={`ellipsis-${index}`}
                    className={cn(
                      "flex min-h-[40px] min-w-[40px] items-center justify-center bg-white p-2",
                      index < pages.length - 1 && "border-r border-[rgba(0,0,45,0.09)]"
                    )}
                  >
                    <span className="text-sm font-semibold leading-5 text-[#60646c]">
                      ...
                    </span>
                  </div>
                );
              }

              const pageNumber = page as number;
              const isCurrentPage = pageNumber === currentPage;

              return (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => !disabled && onPageChange(pageNumber)}
                  disabled={disabled}
                  className={cn(
                    "flex min-h-[40px] min-w-[40px] items-center justify-center p-2",
                    "text-sm font-semibold leading-5",
                    "transition-colors",
                    isCurrentPage
                      ? "bg-[#f9f9fb] text-[#213649]"
                      : "bg-white text-[#60646c] hover:bg-[#fafafa]",
                    index < pages.length - 1 && "border-r border-[rgba(0,0,45,0.09)]",
                    "disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                  )}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          {/* Next Button */}
          <button
            type="button"
            onClick={() => canGoNext && onPageChange(currentPage + 1)}
            disabled={!canGoNext}
            className={cn(
              "flex min-h-[40px] items-center justify-center gap-1.5 px-4 py-2",
              "text-sm font-semibold leading-5 text-[rgba(0,0,0,0.7)]",
              "transition-colors hover:bg-[#fafafa]",
              "disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            )}
          >
            <span>Next</span>
            <ArrowRightIcon className="h-5 w-5 text-[#60646c]" />
          </button>
        </div>

        {/* Mobile Layout */}
        <div className="flex w-full items-center justify-between md:hidden">
          {/* Previous Button */}
          <button
            type="button"
            onClick={() => canGoPrevious && onPageChange(currentPage - 1)}
            disabled={!canGoPrevious}
            aria-label="Previous page"
            className={cn(
              "flex size-[40px] items-center justify-center overflow-clip rounded-md",
              "transition-colors hover:bg-[#fafafa]",
              "disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            )}
          >
            <ArrowLeftIcon className="h-[18px] w-[18px] text-[#60646c]" />
          </button>

          {/* Page Info */}
          <p className="text-sm font-semibold leading-5 text-[rgba(0,0,0,0.7)]">
            Page {currentPage} of {totalPages}
          </p>

          {/* Next Button */}
          <button
            type="button"
            onClick={() => canGoNext && onPageChange(currentPage + 1)}
            disabled={!canGoNext}
            aria-label="Next page"
            className={cn(
              "flex size-[40px] items-center justify-center overflow-clip rounded-md",
              "transition-colors hover:bg-[#fafafa]",
              "disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            )}
          >
            <ArrowRightIcon className="h-[18px] w-[18px] text-[#60646c]" />
          </button>
        </div>
      </div>
    );
  }
);

Pagination.displayName = "Pagination";

export { Pagination };

